import styles from './nested-tracks-panel.module.css'

import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import { LuPause } from '@react-icons/all-files/lu/LuPause'
import { LuPlay } from '@react-icons/all-files/lu/LuPlay'
import React, { useEffect, useState } from 'react'

import api from '@disco/api/server'
import {
  getStemsFromTracksArray,
  getVersionsFromTracksArray,
} from '@disco/utils/nesting'
import { formatDuration } from '@disco/utils/time-utils'
import {
  getTrackWaveform,
  pauseTrack,
  playTrack,
} from '@disco/state-management/actions/MediaActions'
import {
  getTrackPlaybackStatus,
  getTrackWaveformStatus,
  SEARCH_PAGE_LOCATION,
} from '@disco/state-management/selectors/media'
import { PLAYING } from '@disco/constants/MediaStatus'

import type { Track } from 'types/track'

import { Badge, Checkbox, Flex, IconButton, Text } from 'components/ds'
import { sanitizeColumnId } from 'components/ds/DataTable/table-utils'
import Skeleton from 'components/ui/skeleton'
import { useDispatch, useSelector } from 'hooks/use-redux'

import OptimizedWaveform from '../search-columns/waveform-column/OptimizedWaveform'

type VisibleColumn = { id: string }

type NestedTrackRowProps = {
  track: Track
  index: number
  visibleColumns: VisibleColumn[]
  selected: boolean
  onToggleSelect: (_trackId: Track['id']) => void
}

const NestedTrackRow = ({
  track,
  index,
  visibleColumns,
  selected,
  onToggleSelect,
}: NestedTrackRowProps) => {
  const dispatch = useDispatch()
  const trackId = track.id as number

  const waveformStatus = useSelector(state =>
    getTrackWaveformStatus(state, { trackId }),
  )
  const trackStatus = useSelector(state =>
    getTrackPlaybackStatus(state, { trackId }),
  )
  const isPlaying = trackStatus?.playbackStatus === PLAYING

  useEffect(() => {
    if (!track.waveform && !waveformStatus && track.duration) {
      dispatch(getTrackWaveform(trackId))
    }
  }, [track.waveform, waveformStatus, track.duration, trackId, dispatch])

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isPlaying) {
      dispatch(pauseTrack(trackId))
    } else {
      dispatch(
        playTrack(trackId, 'in-app', {
          type: track.type,
          playbackLocation: SEARCH_PAGE_LOCATION,
        }),
      )
    }
  }

  const parsedWaveformData = React.useMemo(() => {
    if (waveformStatus?.waveformData) return waveformStatus.waveformData
    if (!track.waveform) return null
    try {
      return typeof track.waveform === 'string'
        ? JSON.parse(track.waveform)
        : track.waveform
    } catch {
      return null
    }
  }, [track.waveform, waveformStatus?.waveformData])

  return (
    <div className={styles.VersionRow}>
      {visibleColumns.map(col => {
        const colId = sanitizeColumnId(col.id)
        // The panel container is inset by var(--space-3) margin + 1px border;
        // the first cell absorbs that so the rest align with the table columns.
        const width =
          col.id === 'select'
            ? `calc(var(--col-${colId}-size) * 1px - var(--space-3) - 1px)`
            : `calc(var(--col-${colId}-size) * 1px)`

        let content: React.ReactNode = null

        switch (col.id) {
          case 'select':
            content = (
              <Checkbox
                checked={selected}
                onCheckedChange={() => onToggleSelect(track.id)}
                aria-label="Select track"
              />
            )
            break

          case 'Title':
            content = (
              <Flex align="center" gap="2" className={styles.TitleCell}>
                <span className={styles.RowNum}>{index + 1}</span>
                <button
                  className={styles.PlayButton}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                  onClick={handlePlayClick}
                >
                  {isPlaying ? <LuPause size={10} /> : <LuPlay size={10} />}
                </button>
                <Text
                  size="1"
                  weight="medium"
                  truncate
                  className={styles.TrackName}
                >
                  {track.name}
                </Text>
                {track.nesting_label && (
                  <Badge
                    variant="outline"
                    color="gray"
                    size="1"
                    className={styles.NestingLabel}
                  >
                    {track.nesting_label}
                  </Badge>
                )}
              </Flex>
            )
            break

          case 'Duration':
            content = track.duration ? (
              <Text size="1" color="gray">
                {formatDuration(track.duration, 'mm:ss')}
              </Text>
            ) : null
            break

          case 'Waveform':
            content = parsedWaveformData ? (
              <OptimizedWaveform
                track={track}
                parsedWaveformData={parsedWaveformData}
                barGap={2}
                barWidth={1}
                height={20}
                waveformLocation="Search page"
                noAnimation
              />
            ) : (
              <Flex height="20px" />
            )
            break

          case 'NativeTags':
            content =
              track.native_tags && track.native_tags.length > 0 ? (
                <Flex gap="1" wrap="nowrap" overflow="hidden">
                  {track.native_tags.slice(0, 3).map(tag => (
                    <Badge key={tag.id} color="gray" variant="soft" size="1">
                      {tag.name}
                    </Badge>
                  ))}
                </Flex>
              ) : null
            break

          case 'actions':
            content = (
              <IconButton
                variant="ghost"
                size="1"
                color="gray"
                aria-label="More options"
              >
                <LuEllipsis size={14} />
              </IconButton>
            )
            break

          default: {
            const metaVal = (track.metadata as Record<string, unknown>)?.[
              col.id
            ]
            content = metaVal ? (
              <Text size="1" truncate>
                {String(metaVal)}
              </Text>
            ) : null
            break
          }
        }

        const cellClassName =
          col.id === 'select'
            ? `${styles.Cell} ${styles.SelectCell}`
            : col.id === 'Title'
            ? `${styles.Cell} ${styles.TitleColumnCell}`
            : styles.Cell

        return (
          <div key={col.id} className={cellClassName} style={{ width }}>
            {content}
          </div>
        )
      })}
    </div>
  )
}

type SectionProps = {
  label: string
  tracks: Track[]
  visibleColumns: VisibleColumn[]
  selectedIds: Set<Track['id']>
  onToggleSelect: (_trackId: Track['id']) => void
}

const NestedSection = ({
  label,
  tracks,
  visibleColumns,
  selectedIds,
  onToggleSelect,
}: SectionProps) => {
  if (tracks.length === 0) return null
  return (
    <div className={styles.PanelContainer}>
      <div className={styles.SectionHeader}>
        <span className={styles.SectionLabel}>{label}</span>
      </div>
      {tracks.map((track, i) => (
        <NestedTrackRow
          key={track.id}
          track={track}
          index={i}
          visibleColumns={visibleColumns}
          selected={selectedIds.has(track.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  )
}

type Props = {
  track: Track
  colSpan: number
  visibleColumns: VisibleColumn[]
}

const NestedTracksPanel = ({ track, colSpan, visibleColumns }: Props) => {
  const [nestedTracks, setNestedTracks] = useState<Track[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<Track['id']>>(new Set())

  const toggleSelect = React.useCallback((trackId: Track['id']) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(trackId)) {
        next.delete(trackId)
      } else {
        next.add(trackId)
      }
      return next
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setNestedTracks(null)

    api
      .getNestedTracks(track.id)
      .then(tracks => {
        if (!cancelled) {
          setNestedTracks(tracks)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [track.id])

  const versions = nestedTracks ? getVersionsFromTracksArray(nestedTracks) : []
  const stems = nestedTracks ? getStemsFromTracksArray(nestedTracks) : []

  return (
    <td colSpan={colSpan} style={{ padding: 0 }}>
      <div className={styles.PanelWrapper}>
        {loading ? (
          <div className={styles.PanelContainer}>
            <div className={styles.SectionHeader}>
              <Flex width="64px" height="10px">
                <Skeleton type="block" />
              </Flex>
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} className={styles.SkeletonRow}>
                <Flex width="24px" height="24px" flexShrink="0">
                  <Skeleton type="block" />
                </Flex>
                <Flex width="160px" height="12px" flexShrink="0">
                  <Skeleton type="block" />
                </Flex>
                <Flex flexGrow="1" height="12px">
                  <Skeleton type="block" />
                </Flex>
              </div>
            ))}
          </div>
        ) : (
          <>
            <NestedSection
              label="Versions"
              tracks={versions}
              visibleColumns={visibleColumns}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
            <NestedSection
              label="Stems"
              tracks={stems}
              visibleColumns={visibleColumns}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
            />
          </>
        )}
      </div>
    </td>
  )
}

export default NestedTracksPanel
